import type { CSSProperties } from 'react'
import type { ECharts, EChartsOption } from 'echarts'
import type { MergeWithCustomizer } from 'lodash'
import type { ChartEvts } from './declare'
import React, { useEffect, useMemo, useRef } from 'react'
import { EasyECharts } from 'src/components'
import { useDomSize } from 'src/hooks'
import { mergeWith } from 'lodash'
import 'src/styles/components/base/chartSan.less'

type Props = {
  chartData?: any,
  extraOption?: EChartsOption, // effect 渲染后的 setOption
  mergeOption?: EChartsOption, // memo 运行时 merge 的option
  mergeWith?: MergeWithCustomizer,
  onChartReady?: Function,
  onEvts?: ChartEvts,
  style?: CSSProperties
}

const ChartSan = (props: Props) => {
  const chart = useRef<ECharts>()
  const handleChartReady = (chartInstance: any) => {
    chart.current = chartInstance
    props.onChartReady?.(chartInstance)
  }
  useEffect(() => {
    if (props.extraOption && chart.current) {
      chart.current!.setOption(props.extraOption!, undefined, true)
    }
  }, [props.extraOption])

  const refOfDomWrap = useRef(null)
  const wrapSize = useDomSize(refOfDomWrap)
  const nodeWidth = useMemo(() => {
    const { Levels = [] } = props.chartData
    const width = Levels.length && wrapSize.clientWidth ? wrapSize.clientWidth / (Levels.length * 2 - 1) : 130
    return width < 130 ? 130 : width
  }, [wrapSize.clientWidth, props.chartData])

  const dataOption = useMemo<any>(() => {
    if (!props.chartData) return undefined

    const { Title, Links = [], Nodes = [] } = props.chartData
    Nodes.forEach((node: any) => {
      node.itemStyle = {
        color: node.LossRate > 50 ? '#f9517d' : node.LossRate > 30 ? '#ffc45e' : '#25c4ac'
      }
    })
    return {
      title: {
        text: Title,
        left: 38,
        top: 8,
        textStyle: {
          fontSize: 14
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function (params: any) {
          const { name, value, data } = params
          const { views } = data
          if (views && Array.isArray(views) && views.length) {
            return views.map(item => `${item.name}：${item.value}${item.unit}`).join('<br />')
          }
          return `${name}：${value}`
        }
      },
      toolbox: {
        show: true,
        top: 8,
        right: 38,
        feature: {
          restore: { show: true },
          saveAsImage: { show: true }
        }
      },
      series: [
        {
          type: 'sankey',
          data: Nodes,
          links: Links || [],
          left: 18.0,
          top: 50.0,
          right: 18.0,
          bottom: 20.0,
          nodeWidth: nodeWidth,
          nodeGap: 12,
          nodeAlign: 'left',
          emphasis: {
            focus: 'adjacency'
          },
          label: {
            align: 'center',
            verticalAlign: 'middle',
            position: ['50%', '50%'],
            offset: [0, 1],
            fontSize: 12,
            lineHeight: 12,
            distance: 0
          },
          lineStyle: {
            color: 'source',
            curveness: 0.5
          },
          itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent'
          }
        }
      ]
    }
  }, [props.chartData, nodeWidth])

  const chartOption = useMemo(() => {
    if (!props.mergeOption) return dataOption
    return mergeWith(dataOption, props.mergeOption, props.mergeWith)
  }, [dataOption, props.mergeOption])

  const chartHeight = useMemo(() => {
    const { Levels = [], Nodes = [] } = props.chartData

    const minHeight = 12 // value 为 0 时，只有文字的高度，line-height: 12
    const nodeGap = 12 // 节点之间的距离
    const topAndBottomSpace = 50 + 20 // top 和 bottom 的距离
    const minUsage = Math.min(...(Nodes.map((node: any) => node.value).filter((item: any) => item > 0)))

    const totalHeights = Levels.map(({ nodes = [] }) => {
      return nodes.reduce((sum, node: any, index) => {
        return sum + (node.value ? node.value / minUsage * minHeight : minHeight) + (index < nodes.length - 1 ? nodeGap : 0)
      }, 0)
    })
    const maxHeight = Math.max(...totalHeights) + topAndBottomSpace

    return maxHeight > 0 ? (maxHeight > 3000 ? 3000 : maxHeight) : 480
  }, [props.chartData])

  return (
    <div ref={refOfDomWrap} className='san'>
      {
        props.chartData.Levels?.length
          ? (
            <div className='san__header'>
              {
                props.chartData.Levels.map((level: any, index: number, arr: any[]) => {
                  const { value, views = [] } = level
                  const zongliang = views[0] || {}
                  const sunhao = views.slice(1)
                  const getDiv = (item: any) => {
                    const value = `${item.name}：${item.value}${item.unit}`
                    return <div key={value}>{value}</div>
                  }
                  return (
                    <React.Fragment key={`${value}_${index}`}>
                      <div className='san__header-title' style={{ width: nodeWidth }}>
                        {level.name ? <div>{level.name}</div> : null}
                        {getDiv(zongliang)}
                      </div>
                      {
                        sunhao.length && index !== arr.length - 1
                          ? (
                            <div className='san__header-title attach'>
                              {sunhao.map((item: any) => getDiv(item))}
                            </div>
                          )
                          : null
                      }
                    </React.Fragment>
                  )
                })
              }
            </div>
          )
          : null
      }
      <EasyECharts
        notMerge
        option={chartOption}
        onChartReady={handleChartReady}
        onEvents={props.onEvts}
        style={{ height: chartHeight, ...props.style }}
        className='border'
      />
    </div>
  )
}

export default ChartSan